import React, { useCallback, useMemo } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import uploadActions, { uploadReset } from 'store/ducks/upload/actions';
import { useSelector } from 'store';

import Column from '@bexs/dollar/components/GridColumn';
import FieldRow from '@bexs/dollar/components/FieldRow';
import Label from '@bexs/dollar/components/Label';
import UploadBox from '@bexs/dollar/components/UploadBox';
import Input from '@bexs/dollar/components/Input';
import Datepicker from '@bexs/dollar/components/Datepicker';

import FlexBox from '@bexs/dollar/components/Flex';
import * as Yup from 'yup';
import { FileInfo, UploadFormState } from 'pages/Reports/types';
import FeedbackUpload from 'pages/Reports/sections/Upload/FeedbackUpload';
import { CustomButton } from 'styles/global';

const FormBody: React.FC = () => {
  const dispatch = useDispatch();

  const { isSuccess, error, status } = useSelector().upload;

  const validationSchema = Yup.object({
    date: Yup.string().required('Date is required'),
    fileInfo: Yup.array<FileInfo>().required('File is required'),
  });

  const formik = useFormik<UploadFormState>({
    initialValues: {
      date: '',
      formattedDate: '',
      fileInfo: [],
    },
    validationSchema,
    onSubmit: ({ formattedDate, fileInfo }) => {
      dispatch(
        uploadActions.request({
          date: formattedDate,
          file: fileInfo[0],
        })
      );
    },
    validateOnBlur: false,
  });

  const getDateIsInvalid = useMemo(
    () => !!formik.touched.date && !!formik.errors.date,
    [formik.touched.date, formik.errors.date]
  );

  const getFileIsInvalid = useMemo(
    () => !!formik.touched.fileInfo && !!formik.errors.fileInfo,
    [formik.touched.fileInfo, formik.errors.fileInfo]
  );

  const resetForm = useCallback(() => {
    dispatch(uploadReset());
    formik.resetForm();
  }, [dispatch, formik]);

  const setDateValue = (selectedDates: Date[]) => {
    const [dateValue] = selectedDates;
    const date = moment(dateValue).format('DD/MM/YYYY');
    const formattedDate = moment(dateValue).format('YYYY-MM-DD');
    formik.setValues({
      ...formik.values,
      formattedDate,
      date,
    });
  };

  const setFileValue = (files: File[]) => {
    formik.setFieldValue('fileInfo', files);
  };

  const resetFiles = () => formik.setFieldValue('fileInfo', []);

  const datepickerInput = useMemo(
    () => (
      <Input
        autoComplete="off"
        id="date"
        type="text"
        placeholder="DD/MM/YYYY"
        status={getDateIsInvalid ? 'error' : undefined}
        errorLabel={formik.errors.date as string}
        {...formik.getFieldProps('date')}
      />
    ),
    [formik, getDateIsInvalid]
  );

  return (
    <>
      <Column>
        <form onSubmit={formik.handleSubmit} autoComplete="off">
          <FieldRow layout="horizontal" style={{ marginTop: '20px' }}>
            <Label htmlFor="date">File Date</Label>
            <Datepicker
              numberOfVisibleMonths={2}
              renderInput={() => datepickerInput}
              onSelect={setDateValue}
            />
          </FieldRow>
          <FieldRow>
            <UploadBox
              helpText="Accepted formats: .csv"
              layout="vertical"
              isError={getFileIsInvalid}
              onDropFiles={setFileValue}
              onResetFiles={resetFiles}
              errorLabel={formik.errors.fileInfo as string}
              acceptedFormats="csv, application/vnd.ms-excel, text/csv"
            />
          </FieldRow>

          <FlexBox justifyContent="flex-end" style={{ marginTop: '24px' }}>
            <CustomButton type="submit" variation="primary" size="large">
              Upload
            </CustomButton>
          </FlexBox>

          <FeedbackUpload
            resetForm={resetForm}
            isVisible={!!error || isSuccess}
            type={status}
          />
        </form>
      </Column>
      <Column />
    </>
  );
};

export default FormBody;
